import { useState } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { ChartFormatPicker } from './shared/ChartFormatPicker'
import { ChartFrame, withJsonFormat } from './shared/ChartFrame'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { useDalsiGrafStore } from '../../store/useDalsiGrafStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import {
  useDalsiGrafData,
  buildDalsiChartUrl,
  periodToFromTo,
  sranormTooltipRows,
  DALSI_CHIPS,
} from '../../hooks/useDalsiGrafData'
import { useChartFillBust } from '../../hooks/shared/useChartFillBust'
import { useChartToFill } from '../../hooks/shared/useToFill'
import { useAuthStore } from '../../store/useAuthStore'

// Sekce "Další grafy" — produkty tsranorm (Srážky a teploty) a sranorm
// (Kumulativní srážky). Graf + tabulka v tabech, jako Přehled-Graf.
// Viz doc/plans/chart-tsra-norm-client.md.
export function DalsiGrafView({ station, chartType, slug }) {
  const year = useYearPeriodStore(s => s.year)
  const setYear = useYearPeriodStore(s => s.setYear)
  const {
    chip, setChip,
    activeTab, setActiveTab,
    chartFormat, interactive,
  } = useDalsiGrafStore()
  const token = useAuthStore(s => s.token)
  const orientation = useTableOrientationStore(s => s.getOrientation(slug))

  const { rows, columns, isLoading, isError } = useDalsiGrafData(
    station?.gh_id, chartType, year, chip, activeTab === 'data'
  )

  const { from, to } = periodToFromTo(year, chip)
  const fillBust = useChartFillBust()
  const chartUrl = withJsonFormat(
    buildDalsiChartUrl(chartType, station?.gh_id, from, to, /*format*/ null),
    chartFormat
  ) + fillBust

  const [chartMeta, setChartMeta] = useState(null)
  const screenKey = JSON.stringify(['dalsi', chartType, station?.gh_id, year, chip])
  useChartToFill(screenKey, chartMeta, { gran: 'DLY', live: false }, activeTab === 'graf')

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}

        <div className="flex flex-wrap items-start gap-4">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />

          <div className="flex flex-wrap items-start gap-4 ml-auto">
            {activeTab === 'data'
              ? <TableOrientationToggle slug={slug} />
              : <ChartFormatPicker useStore={useDalsiGrafStore} />}
          </div>
        </div>

        {/* Chip řada — rozsah období */}
        <div className="flex flex-wrap gap-1">
          {DALSI_CHIPS.map(c => {
            const isActive = c.id === chip
            return (
              <button
                key={c.id}
                onClick={() => setChip(c.id)}
                className={`px-2 py-0.5 rounded-full text-xs transition-colors border ${
                  isActive
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {c.label}
              </button>
            )
          })}
        </div>

        {/* Záložky */}
        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah záložky */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {activeTab === 'graf' && (
          <div className="overflow-auto">
            <ChartFrame
              key={`${chartUrl}:${token ?? 'anon'}`}
              url={chartUrl}
              gran="dly"
              interactive={interactive}
              onMeta={setChartMeta}
              deriveTooltip={chartType === 'sranorm' ? sranormTooltipRows : undefined}
            />
          </div>
        )}

        {activeTab === 'data' && (
          <>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && (
              <p className="text-red-500 text-sm">Chyba při načítání dat</p>
            )}
            {!isLoading && !isError && (
              <DalsiDataGrid rows={rows} columns={columns} orientation={orientation} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Tabulka ─────────────────────────────────────────────────────────────────
function fmt(v, decimals) {
  if (v == null || (typeof v === 'number' && isNaN(v))) return '—'
  return decimals === 0 ? String(Math.round(v)) : v.toFixed(decimals ?? 1)
}

function fmtDiff(v, decimals) {
  if (v == null || (typeof v === 'number' && isNaN(v))) return '—'
  const s = decimals === 0 ? String(Math.round(Math.abs(v))) : Math.abs(v).toFixed(decimals ?? 1)
  return v < 0 ? `−${s}` : `+${s}`
}

const cb = 'border-b border-r border-gray-200 px-1.5 py-0.5 text-xs tabular-nums whitespace-nowrap text-right'
const thBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1.5 py-1 text-xs font-normal text-center bg-gray-50'

function colClasses(col) {
  if (col.isDiff) return 'text-emerald-700'
  if (col.isNorm) return 'text-blue-500'
  if (col.isCalc) return 'text-gray-500'
  return 'text-gray-700'
}

function cellText(col, v) {
  if (col.isDiff) return fmtDiff(v, col.decimals)
  return fmt(v, col.decimals)
}

function headClasses(col) {
  if (col.isNorm) return 'text-blue-500'
  if (col.isDiff) return 'text-emerald-700'
  if (col.isCalc) return 'text-gray-500'
  return 'text-gray-600'
}

// Oddělovací linka před prvním měsíčním sloupcem
function isFirstMonthly(columns, i) {
  return columns[i].kind === 'monthly' && (i === 0 || columns[i - 1].kind !== 'monthly')
}

function DalsiDataGrid({ rows, columns, orientation = 'normal' }) {
  if (!rows.length) {
    return <p className="text-sm text-gray-400">Žádná data</p>
  }

  if (orientation === 'transposed') {
    return <DalsiDataGridTransposed rows={rows} columns={columns} />
  }

  // Oddělovač měsíců (řádky)
  const monthBorder = i => i > 0 && rows[i].ym !== rows[i - 1].ym

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-l border-l-gray-400 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th rowSpan={2} className="sticky left-0 z-10 bg-gray-50 border-t-2 border-t-gray-400 border-b border-b-gray-200 border-r border-r-gray-400 px-2 py-1 text-gray-500 font-normal whitespace-nowrap">
                Datum
              </th>
              {columns.map((col, ci) => (
                <th
                  key={col.key}
                  className={`${thBase} border-t-2 border-t-gray-400 font-semibold ${headClasses(col)} ${isFirstMonthly(columns, ci) ? 'border-l border-l-gray-400' : ci > 0 ? 'border-l border-l-gray-300' : ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((col, ci) => (
                <th
                  key={`${col.key}-u`}
                  className={`${thBase} text-gray-400 ${isFirstMonthly(columns, ci) ? 'border-l border-l-gray-400' : ci > 0 ? 'border-l border-l-gray-300' : ''}`}
                >
                  {col.unit}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const mb = monthBorder(ri) ? 'border-t-2 border-t-gray-400' : ''
              return (
                <tr key={row.dateKey} className="group">
                  <td className={`sticky left-0 z-10 bg-gray-50 border-b border-b-gray-200 border-r border-r-gray-400 group-last:border-b-0 px-2 py-0.5 text-gray-500 text-right whitespace-nowrap font-mono ${mb}`}>
                    {row.label}
                  </td>
                  {columns.map((col, ci) => {
                    const v = row.values[col.key] ?? null
                    const sep = isFirstMonthly(columns, ci)
                      ? 'border-l border-l-gray-400'
                      : ci > 0 ? 'border-l border-l-gray-300' : ''
                    const monthlyBg = col.kind === 'monthly' ? 'bg-gray-50/60' : 'bg-white'
                    return (
                      <td
                        key={col.key}
                        className={`${cb} group-last:border-b-0 ${monthlyBg} ${sep} ${mb} ${v == null ? 'text-gray-300' : colClasses(col)}`}
                      >
                        {cellText(col, v)}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta: řádky = veličiny, sloupce = dny ──────────────────
const tCb = 'border-b border-r border-gray-200 px-1.5 py-0.5 text-xs tabular-nums whitespace-nowrap text-right'
const tTh = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1.5 py-1 text-xs font-normal text-center bg-gray-50'

function DalsiDataGridTransposed({ rows, columns }) {
  const monthBorder = i => i > 0 && rows[i].ym !== rows[i - 1].ym

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[12rem] whitespace-nowrap">
                Veličina
              </th>
              {rows.map((row, ri) => (
                <th
                  key={row.dateKey}
                  className={`${tTh} border-t-2 border-t-gray-400 border-b-gray-400 text-gray-500 ${monthBorder(ri) ? 'border-l-2 border-l-gray-400' : ''}`}
                >
                  {row.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {columns.map(col => (
              <tr key={col.key} className="group hover:bg-slate-50">
                <td className={`sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left whitespace-nowrap ${col.isNorm ? 'text-blue-500' : col.isDiff ? 'text-emerald-700' : col.isCalc ? 'text-gray-500' : 'text-gray-700'}`}>
                  {col.label}
                  {col.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({col.unit})</span>}
                </td>
                {rows.map((row, ri) => {
                  const v = row.values[col.key] ?? null
                  return (
                    <td
                      key={row.dateKey}
                      className={`${tCb} bg-white group-hover:bg-slate-50 group-last:border-b-0 ${monthBorder(ri) ? 'border-l-2 border-l-gray-400' : ''} ${v == null ? 'text-gray-300' : colClasses(col)}`}
                    >
                      {cellText(col, v)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
